import React, { useState, useEffect } from 'react';
import { getVersionDisplay } from '../utils/version';
import MapboxMap from './MapboxMap';
import OnboardingWizard from './OnboardingWizard';
import SetupTree from './SetupTree';
import AnnualInformation from './AnnualInformation';
import { API_ENDPOINTS } from '../constants/api';
import { openTawkToSupport } from '../utils/tawkToConfig';
import './Dashboard.css';

interface DashboardProps {
  user: {
    id: string;
    name: string;
    email: string;
    organization?: string;
    token: string;
    is_super_user: boolean;
    user_type?: number;
    assigned_organizations?: any[];
  };
  onLogout: () => void;
  selectedOrganizationId?: string | null;
  selectedOrganizationName?: string | null;
  isSuperUser?: boolean;
  userType?: number;
}

const Dashboard: React.FC<DashboardProps> = ({ 
  user, 
  onLogout, 
  selectedOrganizationId, 
  selectedOrganizationName, 
  isSuperUser,
  userType 
}) => {
  const [activeTab, setActiveTab] = useState('annual');
  const [isLoading, setIsLoading] = useState(true);
  const [hasBusinessUnits, setHasBusinessUnits] = useState(false);
  const [showTermsSummaryModal, setShowTermsSummaryModal] = useState(false);
  const [showFullTermsModal, setShowFullTermsModal] = useState(false);

  // Setup data state
  const [businessUnits, setBusinessUnits] = useState<any[]>([]);
  const [farms, setFarms] = useState<any[]>([]);
  const [fields, setFields] = useState<any[]>([]);
  const [organization, setOrganization] = useState<any>(null);
/*
  // Debug logging for tab changes
  useEffect(() => {
    console.log('🔍 Dashboard - Tab changed to:', {
      activeTab,
      selectedOrganizationId,
      isSuperUser
    });
  }, [activeTab, selectedOrganizationId, isSuperUser]);

  // Debug logging for organization context
  useEffect(() => {
    console.log('🔍 Dashboard - selectedOrganizationId changed:', {
      selectedOrganizationId,
      selectedOrganizationName,
      isSuperUser
    });
  }, [selectedOrganizationId, selectedOrganizationName, isSuperUser]);
*/
  // Extract loadData function to reuse it
  const loadData = async () => {
    setIsLoading(true);
    
    try {
      // Determine which organization to load
      const targetOrgId = isSuperUser && selectedOrganizationId ? selectedOrganizationId : user.id;
      
      // Load organization details
      const orgResponse = await fetch(`${API_ENDPOINTS.ORGANIZATIONS}/${targetOrgId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
      });
      
      if (orgResponse.ok) {
        const orgData = await orgResponse.json();
        setOrganization(orgData);
        
        // Check if organization has business units
        const buResponse = await fetch(`${API_ENDPOINTS.BUSINESS_UNITS}?organization_id=${targetOrgId}`, {
          headers: { 'Authorization': `Bearer ${user.token}` },
        });
        
        if (buResponse.ok) {
          const buData = await buResponse.json();
          setBusinessUnits(buData);
          setHasBusinessUnits(buData.length > 0);
          
          // If business units exist, load farms and fields
          if (buData.length > 0) {
            const farmsResponse = await fetch(`${API_ENDPOINTS.FARMS}?organization_id=${targetOrgId}`, {
              headers: { 'Authorization': `Bearer ${user.token}` },
            });
            
            if (farmsResponse.ok) {
              const farmsData = await farmsResponse.json();
              setFarms(farmsData);
            }
            
            const fieldsResponse = await fetch(`${API_ENDPOINTS.FIELDS}?organization_id=${targetOrgId}`, {
              headers: { 'Authorization': `Bearer ${user.token}` },
            });
            
            if (fieldsResponse.ok) {
              const fieldsData = await fieldsResponse.json();
              setFields(fieldsData);
            }
          }
        }
      } else if (orgResponse.status === 401) {
        // Clear all localStorage and logout on unauthorized
        localStorage.clear();
        onLogout();
        return;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when component mounts or organization changes
  useEffect(() => {
    loadData();
  }, [selectedOrganizationId, isSuperUser, user.id, user.token, onLogout]);

  const handleOnboardingComplete = async () => {
    setHasBusinessUnits(true);
    
    // Reload data after onboarding completes to show the newly created business units
    await loadData();
  };

  const handleOnboardingSkip = () => {
    setHasBusinessUnits(true);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="dashboard">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  // Show Onboarding Wizard only if no business units exist
  if (!hasBusinessUnits) {
    return (
      <OnboardingWizard
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
        organizationName={selectedOrganizationName || user.organization || ''}
        userToken={user.token}
      />
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-content-left">
            <h1>RAVAH | CARBON <span className="version-badge">{getVersionDisplay()}</span></h1>
            <div className="copyright-notice">
              © RAVAHTECH, INC. {new Date().getFullYear()} ALL RIGHTS RESERVED
            </div>
          </div>
          {(isSuperUser || userType === 1 || userType === 2) && selectedOrganizationName && (
            <div className="super-user-org">
              <span className="org-label">Working with:</span>
              <span className="org-name">{selectedOrganizationName}</span>
              <button 
                onClick={() => window.location.reload()} 
                className="switch-org-btn"
                title="Switch to a different organization"
              >
                🔄 Switch Organizations
              </button>
            </div>
          )}
          <div className="user-info">
            <span>Welcome, {user.name}</span>
            {isSuperUser && (
              <span className="super-user-badge">Super User</span>
            )}
            {userType === 2 && (
              <span className="super-user-badge">Protocol Manager</span>
            )}
            <button onClick={onLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'annual' ? 'active' : ''}`}
            onClick={() => setActiveTab('annual')}
          >
            Annual Cropped Fields Information
          </button>
          <button
            className={`tab-button ${activeTab === 'setup' ? 'active' : ''}`}
            onClick={() => setActiveTab('setup')}
          >
            Set up Farms and Fields
          </button>
          <button
            className={`tab-button ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
          >
            About RAVAH|CARBON
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'annual' ? (
            <div className="annual-tab">
              <AnnualInformation 
                userToken={user.token}
                organizationName={selectedOrganizationName || user.organization || ''}
                selectedOrganizationId={selectedOrganizationId}
                isSuperUser={isSuperUser}
                engagementLevel={organization?.engagement_level || 1}
              />
            </div>
          ) : activeTab === 'setup' ?  (
            <div className="setup-tab">
              <SetupTree 
                userToken={user.token}
                organizationName={selectedOrganizationName || user.organization || ''}
                selectedOrganizationId={selectedOrganizationId || undefined}
                organization={organization}
                businessUnits={businessUnits}
                farms={farms}
                fields={fields}
                isLoading={false}
                onDataUpdate={(type: string, data: any) => {
                  if (type === 'businessUnits') {
                    setBusinessUnits(data);
                    setHasBusinessUnits(data.length > 0);
                  } else if (type === 'farms') {
                    setFarms(data);
                  } else if (type === 'fields') {
                    setFields(data);
                  } else if (type === 'organization') {
                    setOrganization(data);
                  }
                }}
                onDataRefresh={async () => {
                  // Reload only the setup data without full page refresh
                  console.log('🔄 Refreshing setup data...');
                  await loadData();
                }}
                onOrganizationDeleted={() => {
                  // Account/org no longer exists — return to login / org selector
                  onLogout();
                }}
              />
            </div>
          ) : (activeTab === 'about' ? (
            <div className="about-tab">
              <h1>RAVAH|CARBON Data Integrity Levels</h1>
              
              <div className="about-intro">
                <p>Understanding the different levels of data integrity and verification in the RAVAH|CARBON platform</p>
              </div>

              <div className="about-actions">
                <button 
                  className="btn-primary"
                  onClick={() => setShowTermsSummaryModal(true)}
                >
                  📋 Terms & Conditions Summary
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => setShowFullTermsModal(true)}
                >
                  📄 Full Terms & Conditions
                </button>
                <a 
                  href="https://ravah.ag/solutions-ravahsure-captive-insurance/privacy-policy/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ textDecoration: 'none' }}
                >
                  🔒 Privacy Policy
                </a>
                <button 
                  className="btn-primary"
                  onClick={openTawkToSupport}
                  style={{ backgroundColor: '#2d5a27' }}
                >
                  🎫 Knowledge Base & Support
                </button>
              </div>

              <div className="integrity-levels">
                <div className="integrity-level level-1">
                  <div className="level-header">
                    <div className="level-number">1</div>
                    <h3 className="level-title">Default Data</h3>
                  </div>
                  <div className="level-description">
                    <p>All you need to do for Level 1 is record the crops that you have planted in your fields.The Argonne Labs crop default CI score (GREET) will be applied to all your cropped fields.</p>
                  </div>
                  <div className="level-value">
                    Lowest integrity, GREET Defaults, heavily discounted
                  </div>
                </div>

                <div className="integrity-level level-2">
                  <div className="level-header">
                    <div className="level-number">2</div>
                    <h3 className="level-title">Farmer-adjusted Data</h3>
                  </div>
                  <div className="level-description">
                    <p>For Level 2, you need to input your field data in this App using the Carbon Intensity Data Questionnaire to calculate your own unverified CI Score.</p>
                  </div>
                  <div className="level-value">
                    Self-reported, mostly discounted
                  </div>
                </div>

                <div className="integrity-level level-3">
                  <div className="level-header">
                    <div className="level-number">3</div>
                    <h3 className="level-title">Corroborated Data</h3>
                  </div>
                  <div className="level-description">
                    <p>For Level 3 certified Protocol Managers are assigned to assist you to collect documentation that corroborates the field data that you entered into the App.</p>
                  </div>
                  <div className="level-value">
                    Supported by operational documentation, mid-value
                  </div>
                </div>

                <div className="integrity-level level-4">
                  <div className="level-header">
                    <div className="level-number">4</div>
                    <h3 className="level-title">Verified Data</h3>
                  </div>
                  <div className="level-description">
                    <p>Certified Protocol Managers assist farmers to perform more intensive data collection and verification – optional ISO14064/5 audit.</p>
                  </div>
                  <div className="level-value">
                    Verified data by Protocol Managers, near full value
                  </div>
                </div>

                <div className="integrity-level level-5">
                  <div className="level-header">
                    <div className="level-number">5</div>
                    <h3 className="level-title">High Integrity Data</h3>
                  </div>
                  <div className="level-description">
                    <p>Increased integrity of CI data through an insurance policy resulting in High Integrity Carbon Intensity ("HICI") Certificates (fungible assets).</p>
                  </div>
                  <div className="level-value">
                    Insurance-backed, investment-grade, full value
                  </div>
                </div>
              </div>
            </div>
          ) : null)}
        </div>
      </div>

      {/* Terms & Conditions Summary Modal */}
      {showTermsSummaryModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsSummaryModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Terms & Conditions Summary</h2>
              <button 
                className="terms-modal-close"
                onClick={() => setShowTermsSummaryModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="terms-modal-content">
              <div className="terms-content">
                <div className="terms-summary-header">
                  <h3>👋 Welcome to RAVAH | CARBON</h3>
                  <p>Quick summary of our Terms & Conditions</p>
                </div>
                
                <div className="terms-summary-body">
                <details>
                  <summary>🌱 1. It's Free to Join</summary>
                  <ul>
                    <li>No registration or setup fees. Register your fields at no cost</li>
                    <li>Free Carbon Intensity scoring for Corn, Soybean & Sorghum (more crop types coming soon)</li>
                    <li>RAVAH and our partners cover all the costs to create your Carbon Intensity Certificates</li>
                    <li>Register any of your fields, not just those used for biofuel feedstock</li>
                    <li>You'll have free access to certified Protocol Managers to guide you</li>
                    <li>No extra software investment needed. Our farm management tools are all included</li>
                  </ul>
                </details>

                <details>
                  <summary>🔒 2. Your Data Stays Private</summary>
                  <ul>
                    <li>We designed our RAVAH | CARBON platforms with anonymity and data security at the core</li>
                    <li>RAVAH buys your data on consignment to build and sell Carbon Intensity Certificates</li>
                    <li>Certificates are issued under RAVAH's name — your identity and your farm's identity stays completely private</li>
                  </ul>
                </details>

                <details>
                  <summary>🧾 3. Trusted Certificates</summary>
                  <ul>
                    <li>Our Monitoring, Reporting & Verification system runs on five levels of data integrity - the higher the level, the greater the integrity and higher the certificate value</li>
                    <li>Our Carbon Intensity Certificates are "investment-grade" and highly trusted</li>
                    <li>All your field polygons are registered in RAVAH's name on the METI<sup style={{ fontSize: '0.6em' }}>TM</sup> system, a secure digital clearinghouse</li>
                  </ul>
                </details>

                <details>
                  <summary>💼 4. RAVAH: Your Market Partner - You Earn 80%</summary>
                  <ul>
                    <li>RAVAH promotes millions of acres of verified data to national buyers</li>
                    <li>Buyers trust our ISO 9001 validated process for auditability, transparency and traceability</li>
                    <li><b>You keep 80% of the net sale value</b></li>
                  </ul>
                </details>

                <details>
                  <summary>🌾 5. Plan Ahead (Coming Soon)</summary>
                  <ul>
                    <li>Predict Carbon Intensity Scores before planting</li>
                    <li>Plan ahead and even pre-sell your Carbon Intensity Certificates early in the season</li>
                    <li>RAVAH will offer insurance against crop failure to help protect your expected income</li>
                  </ul>
                </details>

                <details>
                  <summary>📋 6. Your Part</summary>
                  <ul>
                    <li>Grow your crops on the registered fields and aim for Carbon Intensity scores below GREET defaults</li>
                    <li>Keep your supporting records handy — invoices for inputs, harvest data, fuel use, etc. — for audits and verification</li>
                  </ul>
                </details>

                <div className="terms-summary-footer">
                  <p>You're joining a secure, farmer-first platform built to unlock new value from your data 🌾</p>
                </div>
                </div>
              </div>
            </div>
            
            <div className="terms-modal-footer">
              <button
                className="terms-accept-btn"
                onClick={() => setShowTermsSummaryModal(false)}
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Terms & Conditions Modal */}
      {showFullTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowFullTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h2>Full Terms & Conditions</h2>
              <button 
                className="terms-modal-close"
                onClick={() => setShowFullTermsModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="terms-modal-content">
              <div className="terms-content">
                <p>
                  <strong>Full Terms & Conditions Document</strong>
                </p>
                <p>
                  The complete Terms & Conditions document is available as a downloadable file.
                </p>
                <p>
                  <a 
                    href="/RAVAH-CARBON-Terms Conditions v1.1.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="full-terms-link"
                  >
                    📄 Download Full Terms & Conditions Document
                  </a>
                </p>
                <p>
                  <em>Document Version: RAVAH-CARBON-Terms Conditions v1.1</em>
                </p>
              </div>
            </div>
            
            <div className="terms-modal-footer">
              <button
                className="terms-accept-btn"
                onClick={() => setShowFullTermsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 